import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './paymentTypesApi';
import type { PaymentType, Account } from './paymentTypesApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    paymentTypeId: number | null;
}

export function PaymentTypeView({ paymentTypeId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<PaymentType>();

    const [accounts, setAccounts] = useState<Account[]>([]);

    const load = useCallback(async () => {
        detail.setLoading(true);
        detail.setError(null);
        try {
            const accountsData = await api.getAccounts();
            setAccounts(accountsData);
            if (paymentTypeId != null) {
                const data = await api.getPaymentType(paymentTypeId);
                if (!data) throw new Error(`Payment type ${paymentTypeId} returned empty response`);
                detail.setItem(data);
                detail.setIsEditing(false);
            }
        } catch (err) {
            console.error('PaymentTypeView load failed:', err);
            detail.setError(String(err));
        } finally {
            detail.setLoading(false);
        }
    }, [paymentTypeId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = await api.updatePaymentType(item);
            detail.setItem(response);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<PaymentType>
            title={item ? `${item.paymentTransactionCode ?? ''} ${item.paymentTransactionTypeName ?? ''}` : ''}
            isEditing={detail.isEditing}
            isNew={false}
            saving={detail.saving}
            canSave
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
            noItemMessage="No payment type selected."
        >
            {(pt) => (<>
                <FormRow label={t('paymenttype.paymenttypecode')}>
                    <Typography sx={sx13}>{pt.paymentTransactionCode}</Typography>
                </FormRow>

                <FormRow label={t('paymenttype.paymenttypename')}>
                    <Typography sx={sx13}>{pt.paymentTransactionTypeName}</Typography>
                </FormRow>

                <FormRow label={t('paymenttype.account')}>
                    {!detail.isEditing ? (
                        <Typography sx={sx13}>
                            {pt.account ? `${pt.account.accountNumber} ${pt.account.accountName}` : ''}
                        </Typography>
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select
                                value={pt.account?.id ?? ''}
                                onChange={e => {
                                    const a = accounts.find(x => x.id === e.target.value);
                                    detail.update('account', a ?? null);
                                }}
                            >
                                {[...accounts]
                                    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber))
                                    .map(a => (
                                        <MenuItem key={a.id} value={a.id}>{a.accountNumber} {a.accountName}</MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
                </FormRow>
            </>)}
        </DetailViewShell>
    );
}
